import { RootState } from '../redux/store';
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { API_BASE_URL } from '../helpers/common';
import { IAttributeDefinitionMeta } from '../interfaces/IAttribute';

export interface IMantisOutputCatalogField {
  key: string;
  defaultHeading: string;
  sourceType: 'core' | 'attribute';
  isActive: boolean;
  definition?: {
    id: number;
    name: string;
    code: string;
    type: string;
    unit: string | null;
    meta?: IAttributeDefinitionMeta | null;
  };
}

export const companyIntegrationsApi = createApi({
  reducerPath: 'companyIntegrationsApi',
  baseQuery: fetchBaseQuery({
    baseUrl: API_BASE_URL,
    prepareHeaders: async (headers, { getState }) => {
      const token = (getState() as RootState).user.info.accessToken;
      token && headers.set('Authorization', `Bearer ${token}`);
      return headers;
    },
  }),
  endpoints: (build) => ({
    getCompanyIntegrations: build.query<any, void>({
      transformResponse: (response: { data: any }) => response.data,
      query: () => ({
        url: `/company-integrations/list`,
        method: 'GET',
      }),
    }),
    getCompanyIntegrationById: build.query<any, number>({
      transformResponse: (response: { data: any }) => response.data,
      query: (id) => ({
        url: `/company-integrations/${id}`,
        method: 'GET',
      }),
    }),
    getMantisOutputCatalog: build.query<
      IMantisOutputCatalogField[],
      { id: number; type: 'items' | 'receipts' | 'output' }
    >({
      transformResponse: (response: { data: IMantisOutputCatalogField[] }) =>
        response.data,
      query: ({ id, type }) => ({
        url: `/integrations/${id}/output-catalog/${type}`,
        method: 'GET',
      }),
    }),
    updateCompanyIntegration: build.mutation<any, { id: number; formData: any }>({
      transformResponse: (response: { data: any }) => response.data,
      query: ({ id, formData }) => ({
        url: `/company-integrations/${id}`,
        method: 'PATCH',
        body: formData,
      }),
    }),
    createCompanyIntegration: build.mutation<any, any>({
      transformResponse: (response: { data: any }) => response.data,
      query: (data) => ({
        url: `/company-integrations`,
        method: 'POST',
        body: data,
      }),
    }),
    checkIntegrationAccess: build.query<{ hasAccess: boolean }, string>({
      transformResponse: (response: { data: { hasAccess: boolean } }) => response.data,
      query: (key) => ({
        url: `/company-integrations/access/${key}`,
        method: 'GET',
      }),
    }),
    getIntegrationDashboard: build.query<any, string>({
      transformResponse: (response: { data: any }) => response.data,
      query: (key) => ({
        url: `/company-integrations/${key}/dashboard`,
        method: 'GET',
      }),
    }),
  }),
});

export const { 
  useGetCompanyIntegrationsQuery,
  useLazyGetCompanyIntegrationsQuery,
  useGetCompanyIntegrationByIdQuery,
  useGetMantisOutputCatalogQuery,
  useUpdateCompanyIntegrationMutation,
  useCreateCompanyIntegrationMutation,
  useCheckIntegrationAccessQuery,
  useGetIntegrationDashboardQuery,
} = companyIntegrationsApi;
